import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback } from "react";
import { apiCall } from "../Apis/apiCall";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const AllCountries = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [cityName, setCityName] = useState("");
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const fetchCountries = async (
    page = 1,
    perPage = 10,
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("per_page", perPage);
      if (search) queryParams.append("search", search);
      if (sortBy) queryParams.append("sort_by", sortBy);
      if (sortOrder) queryParams.append("sort_order", sortOrder);

      const response = await apiCall(
        "get",
        `/get-all-countries?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const countryList = response.data.data || [];

        setCountries(countryList);
        const pagination = response.data.pagination || {
          total: countryList.length,
          current_page: page,
          per_page: perPage,
          last_page: Math.ceil(countryList.length / perPage) || 1,
        };

        setTotalEntries(pagination.total);
        setCurrentPage(parseInt(pagination.current_page, 10));
        setPerPage(parseInt(pagination.per_page, 10));
        setTotalPages(parseInt(pagination.last_page, 10));
        handleApiResponse(response.data, "success", "GET");
      } else {
        const errorMsg = response?.data?.message || "Failed to load countries.";
        setErrorMessage(errorMsg);
        handleCustomResponse("error", "Failed to load countries.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      const errorMsg =
        error?.response?.data?.message || "Error fetching countries.";
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchCountries = useCallback(
    debounce((page, perPage, search) => {
      fetchCountries(
        page,
        perPage,
        search,
        sortConfig.field,
        sortConfig.direction
      );
    }, 500),
    [sortConfig]
  );

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    fetchCountries(currentPage, perPage, searchQuery, field, newDirection);
  };

  useEffect(() => {
    fetchCountries(
      currentPage,
      perPage,
      searchQuery,
      sortConfig.field,
      sortConfig.direction
    );
  }, [currentPage, perPage]);

  const handleOpenModal = (type, country = null) => {
    setModalType(type);
    setShowModal(true);
    setModalError("");
    setModalSuccess("");
    setModalLoading(false);

    if (type === "delete" && country) {
      setSelectedCountry(country);
    } else if (type === "addCity" && country) {
      setCityName("");
      setSelectedCountry(country);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setCityName("");
    setModalError("");
    setModalSuccess("");
    setModalLoading(false);
    setSelectedCountry(null);
  };

  const handleEditCountry = (country) => {
    navigate(`/edit-country/${country.country_id}`, {
      state: { countryData: country },
    });
  };

  const handleDeleteCountry = async () => {
    try {
      setModalLoading(true);
      setModalError("");

      const response = await apiCall(
        "delete",
        `/delete-country/${selectedCountry.country_id}`,
        null,
        navigate
      );
      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "DELETE");
        handleCloseModal();
        await fetchCountries(
          currentPage,
          perPage,
          searchQuery,
          sortConfig.field,
          sortConfig.direction
        );
      } else {
        handleCustomResponse("error", "Failed to delete country.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddCity = async (e) => {
    e.preventDefault();

    if (!cityName.trim()) {
      handleCustomResponse("error", "City name is required.");
      return;
    }

    try {
      setModalLoading(true);
      setModalError("");

      const response = await apiCall(
        "post",
        "/create-city",
        {
          country_id: selectedCountry.country_id,
          city_name: cityName,
        },
        navigate
      );
      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "POST");
        setCityName("");
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to add city.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
    } finally {
      setModalLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (modalType === "delete") {
      handleDeleteCountry();
    } else if (modalType === "addCity") {
      handleAddCity(e);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedFetchCountries(1, perPage, value);
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1)
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      else if (endPage === totalPages)
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(startEntry + countries.length - 1, totalEntries);
  if (errorMessage) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by country name..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={perPage}
              handlePerPageChange={(value) => {
                setPerPage(value);
                setCurrentPage(1);
              }}
            />
            <div className="ms-auto">
              {hasPermission("insert_countries") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => navigate("/add-country")}
                >
                  <Icon icon="ri:add-line" className="me-2" /> Add Country
                </button>
              )}
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {countries.length === 0 && !loading ? (
            <TableDataNoAvailable message="No countries found." />
          ) : (
            <div className="position-relative">
              <div
                                className="table-responsive scroll-sm"

                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        S.No
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("country_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Country Name</span>
                          <SortIcons
                            field="country_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-md-table-cell"
                        onClick={() => handleSort("country_code")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>ISO Country Code</span>
                          <SortIcons
                            field="country_code"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-md-table-cell"
                        onClick={() => handleSort("currency_code")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Currency Code</span>
                          <SortIcons
                            field="currency_code"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-lg-table-cell"
                        onClick={() => handleSort("currency_symbol")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Currency Symbol</span>
                          <SortIcons
                            field="currency_symbol"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-lg-table-cell"
                        onClick={() => handleSort("timezone")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Timezone</span>
                          <SortIcons field="timezone" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-md-table-cell"
                        onClick={() => handleSort("created_by")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <SortIcons
                            field="created_by"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-lg-table-cell"
                        onClick={() => handleSort("created_datetime")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-lg-table-cell"
                        onClick={() => handleSort("updated_datetime")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Updated Date</span>
                          <SortIcons
                            field="updated_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {countries.map((country, index) => (
                      <tr key={country.country_id || index}>
                        <td className="p-2">{startEntry + index}</td>
                        <td className="p-2">
                          <span className="">
                            {country.country_name ||
                              `Country #${country.country_id}`}
                          </span>
                        </td>
                        <td className="p-2 d-md-table-cell">
                          {country.country_code || "-"}
                        </td>
                        <td className="p-2 d-md-table-cell">
                          {country.currency_code || "-"}
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {country.currency_symbol || "-"}
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {country.timezone || "-"}
                        </td>
                        <td className="p-2 d-md-table-cell">
                          {country.created_by || "-"}
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {country.created_datetime
                            ? new Date(
                                country.created_datetime
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {country.updated_datetime
                            ? new Date(
                                country.updated_datetime
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="p-2">
                          {hasPermission("update_countries") && (
                            <button
                              className="w-32-px h-32-px me-2 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Edit";
                                tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                tooltip.id = "edit-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("edit-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              onClick={() => handleEditCountry(country)}
                            >
                              <Icon icon="lucide:edit" />
                            </button>
                          )}
                          {hasPermission("delete_countries") && (
                            <button
                              className="w-32-px h-32-px me-2 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Delete";
                                tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                tooltip.id = "delete-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("delete-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              onClick={() => handleOpenModal("delete", country)}
                            >
                              <Icon icon="mingcute:delete-2-line" />
                            </button>
                          )}
                          {hasPermission("insert_cities") && (
                            <button
                              className="w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Add City";
                                tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                tooltip.id = "addcity-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("addcity-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              onClick={() =>
                                handleOpenModal("addCity", country)
                              }
                            >
                              <Icon icon="mdi:city-variant-outline" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}

              <PaginationControls
                pagination={{
                  currentPage,
                  perPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "delete"
                  ? "Delete Country"
                  : `Add City in ${selectedCountry?.country_name || "Country"}`}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                disabled={modalLoading}
              ></button>
            </div>
            <div className="modal-body">
              {modalLoading ? (
                <div className="text-center my-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <p className="mt-2">Processing...</p>
                </div>
              ) : modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {selectedCountry?.country_name || "this country"}
                  </strong>
                  ?
                </p>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 mb-20">
                      <label
                        htmlFor="cityName"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        City Name
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="cityName"
                        value={cityName}
                        onChange={(e) => setCityName(e.target.value)}
                        placeholder="Enter city name"
                      />
                    </div>
                  </div>
                </form>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
                disabled={modalLoading}
              >
                Cancel
              </button>
              {modalType === "delete" ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleSubmit}
                  disabled={modalLoading}
                >
                  {modalLoading ? "Please wait..." : "Delete Country"}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary-600"
                  onClick={handleSubmit}
                  disabled={modalLoading}
                >
                  {modalLoading ? "Please wait..." : "Add City"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCountries;
